/**
 * masterApiClient.ts
 *
 * Frontend API client for Master BOM Composer.
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import type {
  BomMasterWoodLine,
  BomMasterHardwareLine,
  MasterComboRow,
  MasterCompareResult,
} from './masterTypes'
import type { AssemblyType } from './types'

export type MasterApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class MasterApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'MasterApiClientError'
    this.code = code
    this.status = status
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as MasterApiError
  throw new MasterApiClientError(
    err?.error?.code ?? 'HTTP_ERROR',
    err?.error?.message ?? `${fallback} (${res.status})`,
    err?.error?.status ?? res.status,
  )
}

export async function getMasterBomLinesApi(productId: string): Promise<{
  wood_lines: BomMasterWoodLine[]
  hardware_lines: BomMasterHardwareLine[]
  wood_duplicate_count: number
  hardware_duplicate_count: number
}> {
  const res = await fetch(`/api/boms/master/${encodeURIComponent(productId)}`)
  const body = await parseJson<
    | {
        ok: true
        wood_lines: BomMasterWoodLine[]
        hardware_lines: BomMasterHardwareLine[]
        wood_duplicate_count: number
        hardware_duplicate_count: number
      }
    | MasterApiError
  >(res)
  throwIfApiError(res, body, 'Lấy danh sách Master BOM thất bại')
  return body as {
    wood_lines: BomMasterWoodLine[]
    hardware_lines: BomMasterHardwareLine[]
    wood_duplicate_count: number
    hardware_duplicate_count: number
  }
}

export async function createMasterWoodLineApi(
  productId: string,
  input: Partial<BomMasterWoodLine>,
): Promise<BomMasterWoodLine> {
  const res = await fetch(`/api/boms/master/${encodeURIComponent(productId)}/wood`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<
    { ok: true; line: BomMasterWoodLine } | MasterApiError
  >(res)
  throwIfApiError(res, body, 'Thêm chi tiết gỗ thất bại')
  return (body as { line: BomMasterWoodLine }).line
}

export async function updateMasterWoodLineApi(
  id: string,
  patch: Partial<BomMasterWoodLine>,
): Promise<BomMasterWoodLine> {
  const res = await fetch(`/api/boms/master/wood/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  const body = await parseJson<
    { ok: true; line: BomMasterWoodLine } | MasterApiError
  >(res)
  throwIfApiError(res, body, 'Cập nhật chi tiết gỗ thất bại')
  return (body as { line: BomMasterWoodLine }).line
}

export async function deleteMasterWoodLineApi(id: string): Promise<boolean> {
  const res = await fetch(`/api/boms/master/wood/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  const body = await parseJson<{ ok: true; deleted: true } | MasterApiError>(res)
  throwIfApiError(res, body, 'Xóa chi tiết gỗ thất bại')
  return true
}

export async function duplicateMasterWoodLineApi(
  id: string,
): Promise<BomMasterWoodLine> {
  const res = await fetch(
    `/api/boms/master/wood/${encodeURIComponent(id)}/duplicate`,
    {
      method: 'POST',
    },
  )
  const body = await parseJson<
    { ok: true; line: BomMasterWoodLine } | MasterApiError
  >(res)
  throwIfApiError(res, body, 'Nhân đôi dòng gỗ thất bại')
  return (body as { line: BomMasterWoodLine }).line
}

export async function createMasterHardwareLineApi(
  productId: string,
  input: Partial<BomMasterHardwareLine>,
): Promise<BomMasterHardwareLine> {
  const res = await fetch(
    `/api/boms/master/${encodeURIComponent(productId)}/hardware`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<
    { ok: true; line: BomMasterHardwareLine } | MasterApiError
  >(res)
  throwIfApiError(res, body, 'Thêm ngũ kim thất bại')
  return (body as { line: BomMasterHardwareLine }).line
}

export async function updateMasterHardwareLineApi(
  id: string,
  patch: Partial<BomMasterHardwareLine>,
): Promise<BomMasterHardwareLine> {
  const res = await fetch(`/api/boms/master/hardware/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  const body = await parseJson<
    { ok: true; line: BomMasterHardwareLine } | MasterApiError
  >(res)
  throwIfApiError(res, body, 'Cập nhật ngũ kim thất bại')
  return (body as { line: BomMasterHardwareLine }).line
}

export async function deleteMasterHardwareLineApi(id: string): Promise<boolean> {
  const res = await fetch(`/api/boms/master/hardware/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  const body = await parseJson<{ ok: true; deleted: true } | MasterApiError>(res)
  throwIfApiError(res, body, 'Xóa ngũ kim thất bại')
  return true
}

export async function duplicateMasterHardwareLineApi(
  id: string,
): Promise<BomMasterHardwareLine> {
  const res = await fetch(
    `/api/boms/master/hardware/${encodeURIComponent(id)}/duplicate`,
    {
      method: 'POST',
    },
  )
  const body = await parseJson<
    { ok: true; line: BomMasterHardwareLine } | MasterApiError
  >(res)
  throwIfApiError(res, body, 'Nhân đôi dòng ngũ kim thất bại')
  return (body as { line: BomMasterHardwareLine }).line
}

export async function getMasterCombosApi(
  productId: string,
): Promise<MasterComboRow[]> {
  const res = await fetch(
    `/api/boms/master/${encodeURIComponent(productId)}/combos`,
  )
  const body = await parseJson<
    { ok: true; combos: MasterComboRow[] } | MasterApiError
  >(res)
  throwIfApiError(res, body, 'Lấy danh sách tổ hợp thất bại')
  return (body as { combos: MasterComboRow[] }).combos
}

export async function publishMasterBomApi(
  productId: string,
  input: {
    variant_id: string
    assembly_type: AssemblyType
    base_version_seq?: number | null
    edit_note?: string | null
    prepared_by?: string | null
    checked_by?: string | null
    notes?: string | null
  },
): Promise<{
  ok: true
  bom_id: string
  bom_family_id: string
  version_seq: number
  version_label: string
  wood_line_count: number
  hardware_line_count: number
}> {
  const res = await fetch(
    `/api/boms/master/${encodeURIComponent(productId)}/publish`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<any>(res)
  throwIfApiError(res, body, 'Publish Master BOM thất bại')
  return body
}

export async function importMasterBomApi(
  productId: string,
  input: {
    variant_id: string
    assembly_type: AssemblyType
  },
): Promise<{
  ok: true
  imported_wood_count: number
  imported_hardware_count: number
  source_bom_id: string
  source_version_label: string
}> {
  const res = await fetch(
    `/api/boms/master/${encodeURIComponent(productId)}/import`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<any>(res)
  throwIfApiError(res, body, 'Nhập từ BOM đã publish thất bại')
  return body
}

export async function compareMasterBomApi(
  productId: string,
  params: {
    source_a?: string
    variant_a: string
    asm_a?: string
    bom_a?: string
    source_b?: string
    variant_b: string
    asm_b?: string
    bom_b?: string
  },
): Promise<MasterCompareResult> {
  const q = new URLSearchParams()
  if (params.source_a) q.set('source_a', params.source_a)
  if (params.variant_a) q.set('variant_a', params.variant_a)
  if (params.asm_a) q.set('asm_a', params.asm_a)
  if (params.bom_a) q.set('bom_a', params.bom_a)
  if (params.source_b) q.set('source_b', params.source_b)
  if (params.variant_b) q.set('variant_b', params.variant_b)
  if (params.asm_b) q.set('asm_b', params.asm_b)
  if (params.bom_b) q.set('bom_b', params.bom_b)

  const res = await fetch(
    `/api/boms/master/${encodeURIComponent(productId)}/compare?${q.toString()}`,
  )
  const body = await parseJson<MasterCompareResult | MasterApiError>(res)
  throwIfApiError(res, body, 'So sánh BOM thất bại')
  return body as MasterCompareResult
}
